import _ from 'lodash';
import SHA256 from 'crypto-js/sha256';
import Base64Encoder from 'crypto-js/enc-base64';

import StringUtils from '../../common/tools/string-utils';
import { getOptions } from '../../common/tools/ctaUtils';
import MODULE_TYPES, { getDefaultModuleConfig } from '../../common/moduleTypes';

/** Tracks down values text value from the field and user input and transforms any HTML parsed elements into proper HTML tags OR strips apart HTML tag declarations.
 * @param {Object} fromObject  - complex object that contains the entire data for a modules form
 * @param {String} field  - the object key we want to track against to find a human readable string for the preview module block
 * @returns {String} - a parsed string value that may OR may not have HTML tags in it
 */
const _getStripped = ({ fromObject, field }) => {
  const _valueFromObject = _.get(fromObject, field, '');
  return StringUtils.stripHTML(_valueFromObject);
};

/** Iterate over the possible fields we can use for a description
 * and choose the last one (right to left) that returns a non-null value
 * note the use of both the module data and the default module data to do
 * our comparison as we only want to use a field when it's been changed
 * @param {Object} moduleData - a complex object that contains the entire data of a module that can be updated with user input
 * @param {Object} defaultData - predefined module data from the template that is used as fall back for any params that might be missing from the user inputed moduleData
 * @return {String} - a string to fill up the ModuleBlock byline, that can be user input or fallback string from module template
 */
const getModuleDescription = ({ type, moduleData, defaultData }) => {
  return _.reduceRight(
    ['label', 'heading', 'byline', 'tagline', 'linkText', 'link'],
    (fallbackDescription, field) => {
      const _moduleDataFieldValue = _getStripped({ fromObject: moduleData, field });

      //parsing the data string and trimming it so that we can properly display a correct preview text
      let _htmlParsedAndTrimedValue = StringUtils.stripAndTrimHTML(_moduleDataFieldValue);

      // correct cta description if link is action
      if (type === MODULE_TYPES.CALLOUT && field === 'link' && _htmlParsedAndTrimedValue) {
        const allOptions = getOptions().allOptions;
        const selectedAction = allOptions.find((action) => action.value === _htmlParsedAndTrimedValue);

        if (selectedAction) {
          _htmlParsedAndTrimedValue = selectedAction.text;
        }
      }

      //If we do end up after the parsing with an empty string, we will default to the modules fallback description
      return !!_htmlParsedAndTrimedValue ? _htmlParsedAndTrimedValue : fallbackDescription;
    },
    defaultData.moduleDescription // this is the fallback - use the data.moduleDescription from moduleTypes.js
  );
};

/**
 * gets ordered page ids from the byPhase object
 */
const orderedPageIds = (byPhase) => _.reduce(byPhase, (acc, pageIds) => acc.concat(pageIds), []);

/**
 * gets ordered page states from page ids a
 */
const orderedPageStates = (orderedPageIds, pagesById) => _.map(orderedPageIds, (pageId) => pagesById[pageId].state);

const DEFAULT_SHA_LENGTH = 22;
//converts the given str to a SHA256 string using sync method.
const _getSHAString = (str, length = DEFAULT_SHA_LENGTH) =>
  SHA256(String(str)).toString(Base64Encoder).substring(0, length);

/**
 * For a given row, it picks the first module that has any meaningful data in it and returns that modules description.
 * @param {object} row - row object as in the layoutConfig
 * @param {array} modules - array of existing modules
 * @returns {string} - the string extracted from the module copy fields
 */
const _getRowInfo = (row, modules) => {
  let rowDescription = '';

  //using .find in this code, so it stops the iteration(s) as soon as it finds a module with proper description in it
  _.find(row.columns, (rowColumn) =>
    _.find(rowColumn.modules, (moduleId) => {
      const moduleObj = _.find(modules, (module) => moduleId === module.id);
      if (moduleObj) {
        rowDescription = getModuleDescription({
          type:        moduleObj.type,
          moduleData:  moduleObj.data,
          defaultData: {},
        });
      }
      return rowDescription;
    })
  );

  return rowDescription;
};

/**
 * Gathers dwell information for rows.
 * Will go through all sections and rows of a page and try to extract some meaningful label, id and sha256id for each row that can be later paired with a dwell insight.
 * @param {object} page - layout page
 * @returns {object} - returns object of {id, sha256Id, label} for each row
 */
const _getPageDwellRowInfo = (page) => {
  const info = [];
  const sections = _.get(page, 'layoutConfig.pageLayout.sections', []);
  const modules = _.get(page, 'layoutConfig.modules', []);
  _.forEach(sections, (section, sectionIdx) => {
    _.forEach(section.rows, (row, rowIdx) => {
      info.push({
        id:       row.rowId,
        sha256Id: _getSHAString(row.rowId),
        label:    _getRowInfo(row, modules) || `section #${sectionIdx + 1}, row #${rowIdx + 1}`,
      });
    });
  });
  return info;
};

/**
 * Returns Page information to be matched with dwell insights
 * @param {array} pages - pages of the experience
 * @param {array} orderedLayoutIds - order of pages
 * @returns {array} - returns array of {id, sha256id, label}
 */
const getDwellPagesInfo = (pages, orderedLayoutIds, includeRowInfo = false) =>
  _.map(orderedLayoutIds, (pageId) => {
    const page = _.find(pages, { id: pageId });
    return {
      id:       pageId,
      sha256Id: _getSHAString(pageId),
      label:    page.name,
      classRef: page.classRef.id,
      ...(includeRowInfo ? { rows: _getPageDwellRowInfo(page) } : false),
    };
  });

const INITIAL_ABANDON_RATE_PERCENT = 100;
/**
 * We have to transform the abandon rate data we get from the server, for easier use with our graphs.
 * This function will turn the abandonRate to percentage of abandon between pages(or rows)
 * @param {array} data - array of objects containing 'remainingARPercent' as a property.
 */
const enhanceDwellAbandonRate = (data) =>
  _.map(data, (d, idx) => {
    if (idx === 0) {
      //first item, suppose we have all 100% of our views
      d.remainingARPercent = INITIAL_ABANDON_RATE_PERCENT;
    } else {
      //next item, calc the remaining percentage by using the previews percentage and dropping the abandonRate
      const prevData = data[idx - 1];
      d.remainingARPercent =
        prevData.remainingARPercent - Math.round(prevData.remainingARPercent * (prevData.abandonRate || 0));
    }
    d.abandonRatePercent = d.abandonRate * 100;
    return d;
  });

export default {
  orderedPageIds,
  orderedPageStates,
  getDwellPagesInfo,
  getModuleDescription,
  enhanceDwellAbandonRate,
};
